import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { Flex, Stack } from '@chakra-ui/layout';
import { useHistory } from 'react-router-dom';
import React from 'react';
import { ALL_FEATURES } from '~/constants';
import { Heading, Menu, StatusBadge } from '~/components';
import { ContractView, StatusExtensaoIdEnum } from '~/types';
import { useExternoCan, useRoleCan } from '~/hooks';
import { CancelContractExtension } from '~/pages/Portal/Contract/CancelContractExtension';
import { EquipmentSuggestionDialog } from '~/pages/Portal/Contract/EquipmentSuggestionDialog';
import { User } from '~/contexts/AuthContext/types';
import { AnalyseContractExtensionModal } from '~/pages/Portal/Contract/AnalyseContractExtensionModal';

interface AnalyseContractExtensionProps {
  id_contrato: string;
  contrato: ContractView;
  handleDownload: (id: string) => void;
  user: User;
}

export const AnalyseContractExtensionBox: React.FC<AnalyseContractExtensionProps> =
  ({
    id_contrato,
    contrato,
    handleDownload,
    user,
  }: AnalyseContractExtensionProps) => {
    const history = useHistory();
    const internoCan = useRoleCan({ roles: ['administrador', 'operacional'] });
    const externoCan = useExternoCan({ roles: ['cliente'] });

    const mostrarMenuOpcoes = (): boolean => {
      return !(
        !internoCan ||
        !contrato.pode_cancelar_extensao ||
        contrato.status_proposta ===
          StatusExtensaoIdEnum.NAO_ELEGIVEL_EXTENSAO ||
        contrato.status_proposta === StatusExtensaoIdEnum.FINALIZADA ||
        contrato.status_proposta ===
          StatusExtensaoIdEnum.ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA ||
        contrato.status_proposta ===
          StatusExtensaoIdEnum.NAO_ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA
      );
    };

    function getMessage(): string {
      switch (contrato.status_proposta) {
        case 0:
          return 'Avalie se o contrato é elegivel para extensão.';
        case 1:
          return externoCan && contrato?.possui_faturas_atraso
            ? 'Regularize os pagamentos pendentes para realizar extensão do Contrato.'
            : externoCan && !contrato?.possui_faturas_atraso
            ? 'Conheça as condições especiais que preparamos para você.'
            : !externoCan && !internoCan
            ? 'Informe o responsável por extensões de Contrato dentro da sua empresa.'
            : !externoCan && internoCan
            ? 'Proposta de renovação enviada ao cliente.'
            : '';
        case 2:
          return contrato?.pode_estender_contrato &&
            contrato?.possui_faturas_atraso
            ? 'Regularize os pagamentos pendentes.'
            : '';
        case 3:
          return 'Aguardando assinatura dos responsáveis.';
        default:
          return '';
      }
    }

    return (
      <Box w="100%" mt="30px">
        <Flex flexDirection="row" justifyContent="space-between" padding="10px">
          <Heading fontSize="md" mb={0}>
            Extensão do Contrato
          </Heading>
        </Flex>
        <Stack>
          <Flex
            bg="#f7f2fa"
            borderRadius="md"
            color="#9246ce"
            flexDirection="column"
            px="10px"
          >
            <Alert
              bg="#f7f2fa"
              borderRadius="md"
              color="#9246ce"
              fontSize="sm"
              py={1}
              px={2}
            >
              <AlertTitle>Status da análise de extensão:</AlertTitle>

              <AlertDescription justifyContent="space-between">
                {(internoCan &&
                  contrato.status_proposta ===
                    StatusExtensaoIdEnum.ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA) ||
                contrato.status_proposta ===
                  StatusExtensaoIdEnum.NAO_ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA ? (
                  <Text color="black">
                    <span style={{ fontWeight: 'bold' }}>
                      {contrato.status_proposta ===
                      StatusExtensaoIdEnum.ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA
                        ? 'ELEGÍVEL'
                        : 'NÃO ELEGÍVEL'}{' '}
                    </span>
                    PARA EXTENSÃO PELA ANÁLISE AUTOMÁTICA
                  </Text>
                ) : (
                  <StatusBadge
                    variant={contrato.statusColorScheme}
                    width="fit-content"
                  >
                    {contrato.formattedStatus}
                  </StatusBadge>
                )}
              </AlertDescription>

              {ALL_FEATURES &&
                internoCan &&
                contrato.status_proposta !==
                  StatusExtensaoIdEnum.AGUARDANDO_ASSINATURA &&
                contrato.status_proposta !==
                  StatusExtensaoIdEnum.TERMO_ASSINADO && (
                  <Flex ml="auto">
                    <AnalyseContractExtensionModal
                      id_contrato={id_contrato}
                      trigger={
                        <Button variant="primary">
                          {contrato.status_proposta ===
                          StatusExtensaoIdEnum.AGUARDANDO_ANALISE_EXTENSAO
                            ? 'Solicitar'
                            : contrato.status_proposta ===
                                StatusExtensaoIdEnum.ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA ||
                              contrato.status_proposta ===
                                StatusExtensaoIdEnum.NAO_ELEGIVEL_EXTENSAO_ANALISE_AUTOMATICA
                            ? 'Revisar'
                            : 'Visualizar'}
                        </Button>
                      }
                    />
                  </Flex>
                )}

              {externoCan &&
                contrato.status_proposta ===
                  StatusExtensaoIdEnum.ELEGIVEL_EXTENSAO &&
                !contrato.possui_faturas_atraso && (
                  <Flex ml="auto">
                    <Button
                      variant="primary"
                      onClick={() =>
                        history.push(
                          `/portal/contracts-extension/${id_contrato}`,
                        )
                      }
                    >
                      Estender Contrato
                    </Button>
                  </Flex>
                )}

              {(contrato.status_proposta ===
                StatusExtensaoIdEnum.AGUARDANDO_ASSINATURA ||
                contrato.status_proposta ===
                  StatusExtensaoIdEnum.TERMO_ASSINADO) && (
                <Flex ml="auto">
                  <Button
                    variant="primary"
                    onClick={() => handleDownload(id_contrato.toString())}
                  >
                    Visualizar proposta de extensão
                  </Button>
                </Flex>
              )}

              {mostrarMenuOpcoes() && (
                <Menu>
                  <Portal>
                    <MenuList zIndex={1000}>
                      <CancelContractExtension
                        id_contrato={id_contrato.toString()}
                        isDisabled={!contrato.pode_cancelar_extensao}
                      />
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </Alert>
          </Flex>
          <Flex
            bg="#f7f2fa"
            borderRadius="md"
            color="#9246ce"
            flexDirection="column"
            px="10px"
          >
            <Alert
              bg="#f7f2fa"
              borderRadius="md"
              color="#9246ce"
              fontSize="sm"
              py={1}
              px={2}
            >
              <AlertTitle>Atenção:</AlertTitle>

              {/* mudar condição para o status da extensão no contrato */}
              <AlertDescription>
                <Text color="black">
                  Este contrato irá encerrar em{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {contrato.formattedVencimentoFinal}
                  </span>
                  . {getMessage()}
                </Text>
              </AlertDescription>
            </Alert>
            {user && user.type === 'Interno' && (
              <EquipmentSuggestionDialog id_contrato={id_contrato} />
            )}
          </Flex>
        </Stack>
      </Box>
    );
  };
